import { z } from 'zod';

/**
 * Health-synthesis section shapes (T-0077 / E07-F08), used by the one
 * Health & readiness report (lib/api/health-readiness-report.ts) and its
 * section editor (components/health/synthesis/section-editor.tsx).
 *
 * T-0246 removed the standalone /health/synthesis endpoints and the
 * fetchers and hooks that called them (nothing used them: the old screen
 * redirects to the Report tab). The report's draft, section edit and
 * publish go through `/health-readiness/report…`.
 *
 * Section taxonomy (matches services/health-synthesis.ts → SECTION_KEYS):
 *   exec_summary | posture | top_risks | recommendations | domain_rollup
 *   | findings_appendix
 */

export const SectionKeySchema = z.enum([
  'exec_summary',
  'posture',
  'top_risks',
  'recommendations',
  'domain_rollup',
  'findings_appendix',
]);
export type SectionKey = z.infer<typeof SectionKeySchema>;

export const SynthesisCitationSchema = z.object({
  ref_kind: z.string(),
  ref_id: z.string(),
  excerpt: z.string().optional(),
  /** The cited record's name, attached by the API at draft time (C2-31). */
  label: z.string().optional(),
});
export type SynthesisCitation = z.infer<typeof SynthesisCitationSchema>;

export const SynthesisSectionSchema = z.object({
  section_id: z.string().uuid(),
  section_key: SectionKeySchema,
  section_order: z.number(),
  content_text: z.string(),
  citations: z.array(SynthesisCitationSchema),
  origin: z.enum(['ai_drafted', 'consultant_edited']),
  edited_at: z.string().nullable(),
  edited_by_user_id: z.string().nullable(),
  edit_reason_tag: z.string().nullable(),
  edit_rationale: z.string().nullable(),
});
export type SynthesisSection = z.infer<typeof SynthesisSectionSchema>;

export interface EditSectionInput {
  content_text: string;
  citations?: SynthesisCitation[];
  override: {
    tag: string;
    rationale: string;
  };
}

/**
 * Human label for the synthesis section keys, ordered per
 * `SECTION_ORDER` in services/health-synthesis.ts.
 */
export const SECTION_LABEL: Record<SectionKey, string> = {
  exec_summary: 'Executive summary',
  posture: 'Overall posture',
  top_risks: 'Top risks',
  recommendations: 'Recommendations',
  domain_rollup: 'Domain rollup',
  findings_appendix: 'Findings appendix',
};

/**
 * Section short description shown under the section title — clarifies
 * the section's editorial intent for consultants who jump straight to
 * a section without re-reading the whole report.
 */
export const SECTION_DESCRIPTION: Record<SectionKey, string> = {
  exec_summary: 'One-paragraph executive abstract written for steering.',
  posture: 'Overall engagement posture with the rationale that drove the call.',
  top_risks: 'Prioritized risks with evidence and recommended owner.',
  recommendations: 'Action-oriented next steps, ordered by urgency.',
  domain_rollup: 'Per-domain assessment summary mirroring the health-domains page.',
  findings_appendix: 'Full findings register attached for traceability.',
};
